/**
 * Campo que aceita o BIP e também o nome — o SKU achado por qualquer um dos
 * dois.
 *
 * ## O beco sem saída que ele resolve
 *
 * Conciliar item de nota fiscal só aceitava código de barras digitado ou
 * bipado. Quem recebe a mercadoria quase nunca tem a peça etiquetada na mão —
 * a etiqueta é impressa DEPOIS da entrada —, então o único caminho era decorar
 * o EAN ou abrir o catálogo em outra aba e copiar. Buscar "runner" e escolher
 * da lista é o que a pessoa tentaria primeiro.
 *
 * ## Por que o leitor de código de barras continua mandando
 *
 * Leitor é um teclado que digita treze dígitos em 50 ms e termina com Enter.
 * Se a lista abrisse durante a bipada, o Enter escolheria a primeira sugestão
 * em vez de submeter o que foi lido — e conciliaria o SKU ERRADO, em silêncio,
 * no caminho que dá entrada em estoque.
 *
 * Por isso a lista NÃO abre quando o valor já é um EAN-13 íntegro: nesse caso
 * o campo se comporta exatamente como antes deste componente existir. É o
 * mesmo `codigoBarrasValido` que a etiqueta usa para decidir se desenha o
 * símbolo — a mesma pergunta, respondida no mesmo lugar.
 *
 * ## Por que a lista vai num portal
 *
 * A ficha da nota vive dentro de uma gaveta com `overflow`, e a linha do item
 * fica no fim de uma lista rolável. Uma lista em fluxo seria cortada pela
 * borda da gaveta — foi o mesmo motivo que levou `MenuDeLinha` e
 * `BotaoExportar` ao portal, e a solução é a mesma: `createPortal` para o
 * `<body>`, posição `fixed` medida a partir do campo.
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent as EventoDeTecla } from 'react'
import { createPortal } from 'react-dom'

import { Icone } from '../../componentes/Icone'
import { formatarMoeda } from '../../plataforma/dinheiro'
import { carregarSKUs, type LinhaSKU } from '../../plataforma/produtos'
import { codigoBarrasValido } from '../etiquetas/codigo-de-barras'

/** Abaixo de dois caracteres a busca traria o catálogo inteiro. */
const MINIMO_PARA_BUSCAR = 2

/** Mais que isto vira rolagem dentro da lista, e rolagem em popover é ruim. */
const MAXIMO_SUGESTOES = 8

export function BuscaDeSKU({
  valor,
  aoDigitar,
  aoEscolher,
  rotuloAcessivel,
  placeholder = 'Bipar, digitar o SKU ou buscar pelo nome',
  desabilitado = false,
  invalido = false,
}: {
  valor: string
  aoDigitar: (texto: string) => void
  /** Chamado quando a pessoa escolhe da lista — nunca durante a digitação. */
  aoEscolher: (sku: LinhaSKU) => void
  rotuloAcessivel: string
  placeholder?: string
  desabilitado?: boolean
  /**
   * Pinta a borda de erro. Vai no `aria-invalid` do INVÓLUCRO, que é de onde a
   * borda e o anel de foco do `composto` saem (CLAUDE.md, "catálogo de
   * componentes") — assim o visual e a acessibilidade não conseguem divergir.
   */
  invalido?: boolean
}) {
  const idLista = useId()
  const campoRef = useRef<HTMLInputElement>(null)

  const [sugestoes, setSugestoes] = useState<LinhaSKU[]>([])
  const [aberta, setAberta] = useState(false)
  const [destacado, setDestacado] = useState(-1)
  const [posicao, setPosicao] = useState({ esquerda: 0, topo: 0, largura: 0 })

  const termo = valor.trim()
  // A bipada não busca: ver o cabeçalho deste arquivo.
  const buscavel = termo.length >= MINIMO_PARA_BUSCAR && !codigoBarrasValido(termo)

  useEffect(() => {
    if (!buscavel) {
      setSugestoes([])
      setAberta(false)
      return
    }

    const controle = new AbortController()
    const relogio = setTimeout(() => {
      carregarSKUs({ busca: termo }, controle.signal)
        .then((dados) => {
          setSugestoes(dados.skus.slice(0, MAXIMO_SUGESTOES))
          setDestacado(-1)
          setAberta(true)
        })
        .catch(() => {
          // Busca é AJUDA, não a operação. Se ela falhar, o campo continua
          // aceitando o código digitado — mostrar um erro aqui atrapalharia
          // quem já está com o EAN na mão e nem queria sugestão.
          setSugestoes([])
          setAberta(false)
        })
    }, 300)

    return () => {
      clearTimeout(relogio)
      controle.abort()
    }
  }, [termo, buscavel])

  // A posição é medida a cada abertura, e a lista fecha ao rolar: um popover
  // `fixed` não acompanha a rolagem do container que o gerou.
  useEffect(() => {
    if (!aberta) return

    const retangulo = campoRef.current?.getBoundingClientRect()
    if (retangulo) {
      setPosicao({
        esquerda: retangulo.left,
        topo: retangulo.bottom + 4,
        largura: retangulo.width,
      })
    }

    const fechar = () => setAberta(false)
    window.addEventListener('scroll', fechar, true)
    window.addEventListener('resize', fechar)
    return () => {
      window.removeEventListener('scroll', fechar, true)
      window.removeEventListener('resize', fechar)
    }
  }, [aberta, sugestoes])

  const escolher = (sku: LinhaSKU) => {
    setAberta(false)
    setDestacado(-1)
    aoEscolher(sku)
  }

  function aoTeclar(evento: EventoDeTecla<HTMLInputElement>) {
    if (!aberta || sugestoes.length === 0) return

    if (evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
      evento.preventDefault()
      const passo = evento.key === 'ArrowDown' ? 1 : -1
      setDestacado((atual) => {
        const proximo = atual + passo
        if (proximo < 0) return sugestoes.length - 1
        if (proximo >= sugestoes.length) return 0
        return proximo
      })
      return
    }

    if (evento.key === 'Enter' && destacado >= 0) {
      // `preventDefault` para o Enter NÃO submeter o formulário de conciliação
      // com o texto digitado: quem tem um item destacado quis escolhê-lo.
      evento.preventDefault()
      const sku = sugestoes[destacado]
      if (sku) escolher(sku)
      return
    }

    if (evento.key === 'Escape') {
      evento.preventDefault()
      setAberta(false)
      setDestacado(-1)
    }
  }

  const idDaOpcao = (indice: number) => `${idLista}-opcao-${indice}`

  return (
    <>
      <label
        className="composto h-8 min-w-0 flex-1 basis-[170px]"
        aria-invalid={invalido ? 'true' : undefined}
      >
        <Icone nome="barcode_scanner" tamanho={16} className="text-texto-fraco" />
        <input
          ref={campoRef}
          role="combobox"
          aria-expanded={aberta}
          aria-controls={idLista}
          aria-autocomplete="list"
          aria-activedescendant={destacado >= 0 ? idDaOpcao(destacado) : undefined}
          aria-label={rotuloAcessivel}
          autoComplete="off"
          disabled={desabilitado}
          value={valor}
          onChange={(evento) => aoDigitar(evento.target.value)}
          onKeyDown={aoTeclar}
          onFocus={() => sugestoes.length > 0 && setAberta(true)}
          // Fechar no `blur`, e não com um overlay cobrindo a tela: o overlay
          // engolia o PRIMEIRO clique em qualquer outro botão — clicar em
          // "Pular" com a lista aberta não fazia nada, e só funcionava no
          // segundo clique. Sair do campo, por clique fora ou por Tab, é
          // exatamente quando a lista deve sumir.
          //
          // Escolher uma opção não passa por aqui: o `onMouseDown` dela
          // cancela o `blur` antes que ele aconteça.
          onBlur={() => {
            setAberta(false)
            setDestacado(-1)
          }}
          placeholder={placeholder}
        />
      </label>

      {aberta &&
        createPortal(
          <ul
            id={idLista}
            role="listbox"
            aria-label="SKUs encontrados"
            style={{
              left: posicao.esquerda,
              top: posicao.topo,
              width: Math.max(posicao.largura, 280),
            }}
            className="fixed z-50 max-h-[300px] overflow-auto rounded-sistema border border-borda bg-superficie py-1 shadow-[0_12px_28px_rgba(15,23,42,0.14)]"
          >
            {/* Zero resultado ABRE a lista assim mesmo, com a razão escrita.
                Não abrir era pior: quem digitasse o nome de um produto que a
                loja ainda não cadastrou veria o campo não fazer nada, e
                concluiria que a busca está quebrada — quando a resposta é
                "este produto não existe no catálogo, e o que falta é
                cadastrá-lo". */}
            {sugestoes.length === 0 && (
              <li role="none" className="px-3 py-2.5">
                <p className="text-[12.5px] leading-[1.5] text-texto-suave">
                  Nenhum SKU do catálogo bate com <span className="font-semibold">“{termo}”</span>.
                  Se o produto ainda não foi cadastrado, o caminho é cadastrá-lo antes — ou pular
                  este item e decidir depois.
                </p>
              </li>
            )}

            {sugestoes.map((sku, indice) => (
              <li key={sku.codigo_barras} role="none">
                <button
                  type="button"
                  role="option"
                  id={idDaOpcao(indice)}
                  aria-selected={indice === destacado}
                  // `onMouseDown` e não `onClick`: o clique tira o foco do
                  // campo antes de o `click` disparar, e o `blur` fecharia a
                  // lista debaixo do cursor.
                  onMouseDown={(evento) => {
                    evento.preventDefault()
                    escolher(sku)
                  }}
                  onMouseEnter={() => setDestacado(indice)}
                  className={`block w-full px-3 py-2 text-left ${
                    indice === destacado ? 'bg-divisoria' : ''
                  }`}
                >
                  <span className="block truncate text-[13px] font-semibold text-tinta">
                    {sku.produto}
                  </span>
                  <span className="block truncate text-[12px] text-texto-suave">
                    {sku.cor} · {sku.tamanho} · {formatarMoeda(sku.preco_centavos)}
                  </span>
                  <span className="tabular block truncate text-[11.5px] text-texto-fraco">
                    {sku.codigo_barras} · {sku.saldo} em estoque
                  </span>
                </button>
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </>
  )
}
